import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import Template from "../../components/Template";
import api from "../../services/api";
import config from "../../config";
import * as XLSX from "xlsx";
import AlertDialog from "../../components/AlertDialog";
import { maskCurrency } from "../../utils/masks";
import { formatDateWithoutHour } from "../../utils/date";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";
import { useNavigation } from "../../context/NavigationContext";

import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
const OrcamentosPrestadorList = () => {
    const navigate = useNavigate();
    const { updateMenu } = useNavigation();

    // Dados
    const [orcamentos, setOrcamentos] = useState([]);
    const [filteredOrcamentos, setFilteredOrcamentos] = useState([]);

    // Controle de paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filtros e pesquisa
    const { filters, setFilters, searchTerm, setSearchTerm, isSearchActive, setIsSearchActive, pageSize, setPageSize, hasRestoredFilters, clearPersistedState } = useListFilterState('orcamentos-prestadores', {
        nomeResponsavel: "",
        obra: "",
        prestador: "",
        dataEnvioInicial: "",
        dataEnvioFinal: "",
    });

    // Dados para os menus suspensos dos filtros de obra e prestador
    const [obras, setObras] = useState([]);
    const [prestadores, setPrestadores] = useState([]);

    // Menus suspensos (para controles gerais)
    const [menuOpen, setMenuOpen] = useState(null);
    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);

    // Modal e alert
    const [showDialog, setShowDialog] = useState(false);
    const [selectedPdf, setSelectedPdf] = useState("");
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [message, setMessage] = useState("");

    // Definição de colunas dinâmicas
    const [columns, setColumns] = useState([
        {
            id: "id",
            label: "ID",
            visible: true,
            formatter: (value) => (
                <Link to={`/orcamentosprestadores/editar/${value}`} className="text-stone-600 hover:underline">
                    {value}
                </Link>
            ),
        },
        {
            id: "valor",
            label: "Valor Cobrado (R$)",
            visible: true,
            formatter: (value) => maskCurrency(Math.round(value * 100)) || "",
        },
        {
            id: "dataEnvio",
            label: "Data de Envio",
            visible: true,
            formatter: (value) => (value ? formatDateWithoutHour(value) : ""),
        },
        {
            id: "nomeResponsavel",
            label: "Responsável",
            visible: true,
            formatter: (value) => value || "",
        },
        {
            id: "obra",
            label: "Obra",
            visible: true,
            formatter: (value, row) => row.obra?.nomeProjeto || "",
        },
        {
            id: "prestador",
            label: "Prestador",
            visible: true,
            formatter: (value, row) => (row.fornecedor?.nomeFornecedor || "") + " - " + row.fornecedor?.cpfCnpj,
        },
    ]);

    /**
     * Busca orçamentos no backend
     */
    // Filtros efetivamente aplicados pelo botao "Filtrar". Sao reenviados em toda
    // troca de pagina e de "resultados por pagina": antes a paginacao refazia a
    // busca sem filtro nenhum e a lista voltava silenciosamente ao conjunto completo.
    const appliedFiltersRef = useRef({});

    // Descarta respostas de filtros que já foram superados por outro mais novo:
    // sem isto, a resposta lenta de um filtro anterior sobrescrevia a tela.
    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchOrcamentos = async (page, size) => {
        try {
            const params = new URLSearchParams({ page, pageSize: size });
            Object.entries(appliedFiltersRef.current).forEach(([chave, valor]) => {
                if (valor !== undefined && valor !== null && valor !== "") params.set(chave, valor);
            });
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/orcamentosprestador?${params.toString()}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            setOrcamentos(response.data.records);
            setTotalRecords(response.data.total);
            setPageSize(size);
        } catch (error) {
            console.error("Erro ao carregar orçamentos:", error);
        }
    };

    // Carrega obras e prestadores para os filtros (menu suspenso)
    useEffect(() => {
        const fetchObrasEPrestadores = async () => {
            try {
                const [obrasRes, prestadoresRes] = await Promise.all([
                    api.get("/obras/listar/todas"),
                    api.get("/fornecedores/prestadores/todos"),
                ]);
                setObras(obrasRes.data);
                setPrestadores(prestadoresRes.data);
            } catch (error) {
                console.error("Erro ao carregar obras ou prestadores:", error);
            }
        };
        fetchObrasEPrestadores();
    }, []);

    // Efeito para carregar dados iniciais
    useEffect(() => {
        if (hasRestoredFilters()) {
            applyFilters();
            return;
        }
        fetchOrcamentos(currentPage, pageSize);
    }, [currentPage]);

    // Efeito para filtrar localmente, caso use searchTerm
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredOrcamentos(orcamentos);
        } else {
            const term = searchTerm.toLowerCase();
            setFilteredOrcamentos(
                orcamentos.filter(
                    (orc) =>
                        (orc.nomeResponsavel && orc.nomeResponsavel.toLowerCase().includes(term)) ||
                        (orc.obra?.nomeProjeto && orc.obra.nomeProjeto.toLowerCase().includes(term)) ||
                        (orc.fornecedor?.nomeFornecedor && orc.fornecedor.nomeFornecedor.toLowerCase().includes(term))
                )
            );
        }
    }, [searchTerm, orcamentos]);

    /**
     * Filtro avançado
     */
    const fetchFilteredOrcamentos = async (query) => {
        try {
            const params = new URLSearchParams(query).toString();
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/orcamentosprestador?${params}`);
            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            setOrcamentos(response.data.records);
            setTotalRecords(response.data.total);
            setCurrentPage(1);
        } catch (error) {
            console.error("Erro ao aplicar filtros:", error);
        }
    };

    // Aplica os filtros do menu suspenso
    const applyFilters = () => {
        appliedFiltersRef.current = { ...filters };
        setCurrentPage(1);
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };
        fetchFilteredOrcamentos(query);
    };

    // Limpa os filtros
    const clearFilters = () => {
        clearPersistedState();
        appliedFiltersRef.current = {};
        setSearchTerm("");
        setCurrentPage(1);
        setFilters({
            nomeResponsavel: "",
            obra: "",
            prestador: "",
            dataEnvioInicial: "",
            dataEnvioFinal: "",
        });
        fetchOrcamentos(1, pageSize);
    };

    // Pesquisa rápida
    const handleSearch = () => {
        appliedFiltersRef.current = { ...filters };
        setCurrentPage(1);
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };
        fetchFilteredOrcamentos(query);
    };

    // Alterna visibilidade de colunas
    const toggleColumnVisibility = (id) => {
        setColumns((prevColumns) =>
            prevColumns.map((col) =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    // Abre/fecha menus suspensos dos controles
    const handleMenuClick = (event) => {
        event.stopPropagation();
    };
    const handleClickOutside = (event) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target) &&
            buttonFiltrarRef.current &&
            !buttonFiltrarRef.current.contains(event.target)
        ) {
            setMenuOpen(null);
        }
    };
    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    // Navegar para tela de cadastro
    const handleNavigateCadastrar = (e) => {
        e.preventDefault();
        navigate("/orcamentosprestadores/cadastrar");
    };

    // Exportar para Excel
    const exportToExcel = () => {
        if (orcamentos.length === 0) {
            setMessage("Não há dados para exportar!");
            setShowSuccessDialog(true);
            return;
        }

        // Colunas monetárias que precisam ser convertidas
        const monetaryColumns = ['valor']; // Substitua pelos IDs reais das colunas

        const dataToExport = orcamentos.map((orc) => {
            const row = {};
            columns.forEach((col) => {
                if (col.visible) {
                    let value;
                    if (col.id === "obra") {
                        value = orc.obra?.nomeProjeto || "";
                    } else if (col.id === "prestador") {
                        value = orc.fornecedor?.nomeFornecedor || "";
                    } else if (col.formatter) {
                        value = col.formatter(orc[col.id], orc);
                    } else {
                        value = orc[col.id];
                    }

                    // Conversão para número, se for coluna monetária
                    if (monetaryColumns.includes(col.id)) {
                        value = Number(String(value)
                            .replace(/[R$\s.]/g, '')
                            .replace(',', '.')
                        );
                    }

                    row[col.label] = value;
                }
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Formatação explícita das colunas monetárias no Excel
        const columnLabels = columns.filter(col => col.visible).map(col => col.label);
        monetaryColumns.forEach((colId) => {
            const colIndex = columnLabels.indexOf(columns.find(c => c.id === colId)?.label);
            if (colIndex >= 0) {
                const columnLetter = XLSX.utils.encode_col(colIndex);
                const range = XLSX.utils.decode_range(worksheet['!ref']);

                for (let i = range.s.r + 1; i <= range.e.r; i++) {
                    const cellAddress = `${columnLetter}${i + 1}`;
                    if (worksheet[cellAddress]) {
                        worksheet[cellAddress].t = 'n';
                        worksheet[cellAddress].z = '#,##0.00';
                    }
                }
            }
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "OrcamentosPrestador");
        XLSX.writeFile(workbook, "orcamentosprestador.xlsx");
    };


    // Paginação
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);

    const pageWindowSize = 5;
    // Mantem a janela cheia tambem nas ultimas paginas (antes ela encolhia).
    const startPage = Math.max(1, Math.min(currentPage - Math.floor(pageWindowSize / 2), totalPages - pageWindowSize + 1));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);
    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    // Permissões
    const canExport = usePermission("EXPORTAR_ORCAMENTO_PRESTADOR_OBRA");
    const canCreate = usePermission("CRIAR_ORCAMENTO_PRESTADOR_OBRA");

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                {/* Título */}
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="text-stone-800 text-xl md:text-2xl font-medium">
                        Orçamentos de Prestador
                    </div>
                </div>

                {/* Linha de controles */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                    {/* ESQUERDA: "Ver X Resultados" */}
                    <div className="flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium">Ver</div>
                        <select
                            className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700"
                            value={pageSize}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value, 10);
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                                fetchOrcamentos(1, newPageSize);
                            }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium">Resultados</div>
                    </div>

                    {/* DIREITA: Colunas, Filtro, Pesquisa, Exportar, Cadastrar */}
                    <div className="flex flex-wrap items-center gap-2 md:gap-4">
                        {/* Botão de Colunas */}
                        <div
                            ref={buttonRef}
                            className="relative flex items-center gap-1 cursor-pointer"
                            onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}
                        >
                            <div className="flex items-center gap-2">
                                <img className="w-6 h-6" src="/images/icon/columns.png" alt="Colunas" />
                                <div className="text-stone-600 text-sm font-medium">Colunas</div>
                            </div>
                            <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="Seta" />
                            {menuOpen === "colunas" && (
                                <div
                                    className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50"
                                    style={{ zIndex: 9999 }}
                                    onClick={handleMenuClick}
                                >
                                    <div className="p-4 flex flex-col gap-3">
                                        {columns.map((col) => (
                                            <div key={col.id} className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id={col.id}
                                                    checked={col.visible}
                                                    onChange={() => toggleColumnVisibility(col.id)}
                                                />
                                                <label htmlFor={col.id} className="text-sm text-gray-700">
                                                    {col.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Botão de Filtro */}
                        <div
                            ref={buttonFiltrarRef}
                            className="relative flex items-center gap-1 cursor-pointer"
                            onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")}
                        >
                            <div className="flex items-center gap-2">
                                <img className="w-8 h-8 px-0.5 py-[3px]" src="/images/icon/filter.png" alt="Filtrar" />
                                <div className="text-stone-600 text-sm font-medium">Filtrar</div>
                            </div>
                            <img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" alt="Seta" />
                            {menuOpen === "filtro" && (
                                <div
                                    className="absolute top-full left-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border border-gray-300"
                                    style={{ zIndex: 9999 }}
                                    onClick={handleMenuClick}
                                >
                                    <div className="flex flex-col gap-4">
                                        <div className="self-stretch inline-flex items-center gap-2.5">
                                            <div className="grow text-stone-800 text-xl font-medium">Filtrar</div>
                                        </div>

                                        {/* Filtro: Nome do Responsável */}
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">Nome do Responsável</div>
                                            <input
                                                type="text"
                                                name="nomeResponsavel"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o nome do responsável"
                                                value={filters.nomeResponsavel}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, nomeResponsavel: e.target.value })
                                                }
                                            />
                                        </div>

                                        {/* Filtro: Obra (menu suspenso) */}
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">Obra</div>
                                            <select
                                                name="obra"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                value={filters.obra}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, obra: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {obras.map((o) => (
                                                    <option key={o.id} value={o.id}>
                                                        {o.nomeProjeto}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Filtro: Prestador (menu suspenso) */}
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">Prestador</div>
                                            <select
                                                name="prestador"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                value={filters.prestador}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, prestador: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {prestadores.map((p) => (
                                                    <option key={p.id} value={p.id}>
                                                        {p.nomeFornecedor + " - " + p.cpfCnpj}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Filtro: Data de Envio */}
                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium">Data de Envio</div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="date"
                                                    name="dataEnvioInicial"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataEnvioInicial}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataEnvioInicial: e.target.value })
                                                    }
                                                />
                                                <input
                                                    type="date"
                                                    name="dataEnvioFinal"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataEnvioFinal}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataEnvioFinal: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="self-stretch flex items-center justify-between gap-4 mt-4">
                                            <button
                                                className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 flex justify-center items-center gap-2"
                                                onClick={clearFilters}
                                            >
                                                <img className="w-4 h-4" src="/images/icon/eraser.png" alt="Limpar" />
                                                <div className="text-stone-500 text-sm font-medium">Limpar</div>
                                            </button>
                                            <button
                                                className="px-4 py-2 w-full bg-stone-600 rounded-md flex justify-center items-center gap-2"
                                                onClick={applyFilters}
                                            >
                                                <img className="w-4 h-4" src="/images/icon/filter-white.png" alt="Filtrar" />
                                                <div className="text-white text-sm font-medium">Filtrar</div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Pesquisar */}
                        <div className="flex items-center gap-2">
                            {!isSearchActive ? (
                                <div
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={() => setIsSearchActive(true)}
                                >
                                    <img className="w-10 h-10" src="/images/icon/search.png" alt="Pesquisar" />
                                    <div className="text-stone-600 text-sm font-medium">Pesquisar</div>
                                </div>
                            ) : (
                                <div className="h-10 flex items-center gap-2">
                                    <div className="h-9 px-3 py-2 bg-white rounded-md border border-slate-300 flex items-center">
                                        <input
                                            type="text"
                                            placeholder="Pesquisar"
                                            className="w-full text-slate-400 text-base font-normal focus:outline-none"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <img
                                            className="w-6 h-6 p-[3.33px] cursor-pointer"
                                            src="/images/icon/close.png"
                                            alt="Fechar"
                                            onClick={() => {
                                                setIsSearchActive(false);
                                                setSearchTerm("");
                                            }}
                                        />
                                    </div>
                                    <button
                                        className="px-4 py-2 bg-stone-600 rounded-md flex items-center gap-2"
                                        onClick={() => {
                                            // Caso queira filtrar via backend, chame fetchFilteredOrcamentos
                                        }}
                                    >
                                        <img className="w-4 h-4" src="/images/icon/search-white.png" alt="Pesquisar" />
                                        <div className="text-white text-sm font-medium">Pesquisar</div>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Exportar */}
                        {canExport && (
                            <button
                                type="button"
                                className="flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 items-center gap-2 cursor-pointer"
                                onClick={exportToExcel}
                            >
                                <img className="w-4 h-4" src="/images/icon/upload.png" alt="Exportar" />
                                <div className="text-stone-600 text-sm font-medium">Exportar</div>
                            </button>
                        )}

                        {/* Cadastrar */}
                        {canCreate && (
                            <button
                                type="button"
                                className="px-4 py-2 bg-stone-600 text-white rounded-md flex items-center gap-2"
                                onClick={handleNavigateCadastrar}
                            >
                                <img className="w-4 h-4" src="/images/icon/plus-circle.png" alt="Cadastrar" />
                                <div className="text-white text-sm font-medium">Cadastrar</div>
                            </button>
                        )}
                    </div>
                </div>
                {/* Fim da linha de controles */}

                {/* Tabela */}
                <div className="overflow-x-auto md:overflow-x-visible">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                {columns
                                    .filter((col) => col.visible)
                                    .map((col) => (
                                        <th
                                            key={col.id}
                                            className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold"
                                        >
                                            {col.label}
                                        </th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrcamentos.map((orc) => (
                                <tr key={orc.id}>
                                    {columns
                                        .filter((col) => col.visible)
                                        .map((col) => (
                                            <td
                                                key={`${orc.id}-${col.id}`}
                                                className="px-4 py-2 border border-slate-200 text-slate-700 text-base"
                                            >
                                                {col.formatter ? col.formatter(orc[col.id], orc) : orc[col.id]}
                                            </td>
                                        ))}
                                </tr>
                            ))}
                            {filteredOrcamentos.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={columns.filter((c) => c.visible).length}
                                        className="px-4 py-2 text-center text-slate-500"
                                    >
                                        Nenhum registro encontrado.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Paginação */}
                <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                    <div className="grow text-slate-500 text-base">
                        Mostrando de {startRecord} até {endRecord} de {totalRecords} registros.
                    </div>
                    <div className="flex items-start gap-4">
                        {/* Botão Anterior */}
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] flex justify-center items-center ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"
                                }`}
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <img className="w-6 h-6" src="/images/icon/keyboard-arrow-left.png" alt="Anterior" />
                        </button>
                        {/* Páginas */}
                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-10 h-10 rounded flex justify-center items-center text-base font-semibold ${page === currentPage ? "bg-stone-600 text-white" : "bg-gray-200 text-gray-600"
                                    }`}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        ))}
                        {/* Botão Próximo */}
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] flex justify-center items-center ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"
                                }`}
                            disabled={currentPage >= totalPages || totalPages === 0}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <img className="w-6 h-6" src="/images/icon/keyboard-arrow-right.png" alt="Próximo" />
                        </button>
                    </div>
                </div>

                {/* Modal de PDF (caso utilize) */}
                {showDialog && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                        <div className="rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center gap-4">
                                    <a
                                        href={config.urlArquivo(selectedPdf)}
                                        download
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <img className="w-6 h-6" src="/images/icon/download.png" alt="Download" />
                                    </a>
                                </div>
                                <img
                                    className="w-6 h-6 cursor-pointer"
                                    src="/images/icon/x.png"
                                    onClick={() => setShowDialog(false)}
                                    alt="Fechar"
                                />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                                <iframe
                                    src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                    className="w-full h-full border-0"
                                    style={{ border: "none" }}
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Alerta de sucesso */}
                <AlertDialog
                    isVisible={showSuccessDialog}
                    message={message}
                    onClose={() => setShowSuccessDialog(false)}
                />
            </div>
        </Template>
    );
};

export default OrcamentosPrestadorList;
